import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, BadgeCheck, Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import Seo from "@/components/Seo";
import { Reveal, SectionHead, EASE } from "@/components/Reveal";
import { PropertyCard } from "@/components/PropertyCard";
import { ValuationTeaserForm } from "@/components/LeadForms";
import { useLanguage } from "@/context/LanguageContext";
import { useSiteContent } from "@/context/SiteContext";
import { useTestimonials } from "@/hooks/useTestimonials";
import { useListings } from "@/hooks/useListings";
import { neighborhoods } from "@/data/neighborhoods";
import { developments as fallbackDevelopments } from "@/data/developments";
import { useDevelopments } from "@/hooks/useDevelopments";
import { fmtPrice } from "@/utils/money";

const HERO_IMG = "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=2400&q=85";

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({ y: "0%", transition: { duration: 1, delay: 0.25 + i * 0.14, ease: EASE } }),
};

const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section ref={ref} data-testid="home-hero" className="relative h-[100svh] min-h-[620px] overflow-hidden bg-ink">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Luxury South Florida estate at dusk" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-ink/60 to-transparent" />
      <div className="absolute inset-0 grain-overlay" />

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-24 sm:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7, ease: EASE }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-gold" />
          <p data-testid="hero-badge" className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-gold font-semibold">
            {t.hero.badge}
          </p>
        </motion.div>

        <h1 className="font-serif text-cream text-[13vw] sm:text-7xl lg:text-[6.5rem] leading-[1.02] tracking-tight max-w-5xl">
          {lines.map((l, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className={`block ${i === 2 ? "italic text-gold" : ""}`}>
                {l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
          className="mt-6 max-w-xl text-cream/70 text-base sm:text-lg leading-relaxed"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/listings"
            data-testid="hero-browse-homes-btn"
            className="group inline-flex items-center gap-3 bg-gold hover:bg-gold-dark text-ink font-semibold text-sm tracking-wide px-8 py-4 rounded-full transition-colors duration-300"
          >
            {t.hero.cta1}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/valuation"
            data-testid="hero-valuation-btn"
            className="inline-flex items-center gap-3 border border-cream/40 hover:border-gold text-cream hover:text-gold font-semibold text-sm tracking-wide px-8 py-4 rounded-full transition-colors duration-300"
          >
            {t.hero.cta2}
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 right-6 sm:right-10 z-10 flex items-center gap-3 text-cream/50"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown className="w-4 h-4 text-gold" />
        </motion.span>
      </motion.div>
    </section>
  );
};

const StatsBar = () => {
  const { lang, t } = useLanguage();
  const { content } = useSiteContent();
  const stats = content?.stats?.length
    ? content.stats.map((s) => ({ value: s.value, label: s.label?.[lang] || s.label?.en }))
    : t.stats.items;
  const areas = content?.marquee?.[lang]?.length ? content.marquee[lang] : t.marquee;
  return (
    <section data-testid="stats-bar" className="bg-ink border-t border-white/10 relative grain-overlay">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8 items-center">
        {stats.map((s, i) => (
          <Reveal key={i} delay={i * 0.1} className="text-center">
            <p data-testid={`stat-value-${i}`} className="font-serif text-3xl sm:text-4xl lg:text-5xl text-gold">{s.value}</p>
            <p className="mt-2 text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-cream/50 font-medium">{s.label}</p>
          </Reveal>
        ))}
        <Reveal delay={0.3} className="text-center col-span-2 lg:col-span-1">
          <p data-testid="stat-areas-label" className="text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">{t.stats.areasLabel}</p>
          <p className="mt-2 font-serif italic text-cream/80 text-sm sm:text-base leading-relaxed">{areas.join(" · ")}</p>
        </Reveal>
      </div>
    </section>
  );
};

const AgentIntro = () => {
  const { t } = useLanguage();
  return (
    <section data-testid="agent-intro" className="bg-cream py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <Reveal className="relative">
          <div className="absolute -top-4 -left-4 w-full h-full border border-gold/50 pointer-events-none" />
          <div className="relative overflow-hidden">
            <img
              src="/images/carlos-headshot.jpg"
              alt="Carlos Vindel, Miami real estate agent"
              loading="lazy"
              className="w-full aspect-[4/5] object-cover object-top"
            />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink/80 to-transparent p-6 pt-16">
              <p className="font-serif text-cream text-2xl">{t.intro.badge}</p>
              <p className="text-gold text-[11px] uppercase tracking-[0.25em] mt-1">{t.intro.badgeSub}</p>
            </div>
          </div>
        </Reveal>
        <div>
          <SectionHead overline={t.intro.overline} title={t.intro.title} />
          <Reveal delay={0.2}>
            <p className="mt-6 text-[#525D6B] leading-relaxed">{t.intro.bio1}</p>
          </Reveal>
          <Reveal delay={0.28}>
            <p className="mt-4 text-[#525D6B] leading-relaxed">{t.intro.bio2}</p>
          </Reveal>
          <Reveal delay={0.36}>
            <Link
              to="/about"
              data-testid="intro-learn-more-link"
              className="group mt-8 inline-flex items-center gap-3 text-ink font-semibold text-sm tracking-wide link-underline-gold"
            >
              {t.intro.link}
              <ArrowRight className="w-4 h-4 text-gold transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const FeaturedListings = () => {
  const { t } = useLanguage();
  const { listings } = useListings();
  const featured = listings.filter((l) => l.featured);
  return (
    <section data-testid="featured-listings" className="bg-white py-24 sm:py-32 border-y border-sand/60">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <SectionHead overline={t.listingsSection.overline} title={t.listingsSection.title} subtitle={t.listingsSection.subtitle} />
          <Reveal delay={0.2}>
            <Link to="/listings" data-testid="featured-view-all-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-ink link-underline-gold shrink-0">
              {t.listingsSection.viewAll}
              <ArrowRight className="w-4 h-4 text-gold transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((l, i) => (
            <PropertyCard key={l.id} listing={l} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ValuationTeaser = () => {
  const { t } = useLanguage();
  return (
    <section data-testid="valuation-teaser" className="bg-emerald grain-overlay relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <SectionHead dark overline={t.valuationTeaser.overline} title={t.valuationTeaser.title} subtitle={t.valuationTeaser.subtitle} />
        <Reveal delay={0.15} className="bg-cream/95 p-7 sm:p-9 shadow-[0_24px_64px_rgba(0,0,0,0.25)]">
          <ValuationTeaserForm />
        </Reveal>
      </div>
    </section>
  );
};

const NeighborhoodsPreview = () => {
  const { lang, t } = useLanguage();
  return (
    <section data-testid="neighborhoods-preview" className="bg-cream py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-14">
          <SectionHead overline={t.neighborhoodsSection.overline} title={t.neighborhoodsSection.title} subtitle={t.neighborhoodsSection.subtitle} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {neighborhoods.map((n, i) => (
            <Reveal key={n.slug} delay={(i % 3) * 0.08}>
              <Link to={`/neighborhoods/${n.slug}`} data-testid={`neighborhood-card-${n.slug}`} className="group relative block aspect-[4/3] overflow-hidden">
                <img
                  src={n.image}
                  alt={`${n.name}, South Florida`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold">{n.tagline[lang]}</p>
                  <div className="mt-1.5 flex items-end justify-between gap-3">
                    <p className="font-serif text-cream text-2xl">{n.name}</p>
                    <span className="flex items-center gap-1.5 text-cream/70 text-xs font-medium opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                      {t.neighborhoodsSection.readMore} <ArrowRight className="w-3.5 h-3.5 text-gold" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const TestimonialsReel = () => {
  const { lang, t } = useLanguage();
  const { testimonials } = useTestimonials();
  return (
    <section data-testid="testimonials-reel" className="bg-white py-24 sm:py-32 border-y border-sand/60">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="mb-12 text-center">
          <SectionHead align="center" overline={t.testimonialsSection.overline} title={t.testimonialsSection.title} />
        </div>
        <Reveal delay={0.15}>
          <Carousel opts={{ loop: true }} data-testid="testimonials-carousel">
            <CarouselContent>
              {testimonials.map((tst) => (
                <CarouselItem key={tst.id} data-testid={`testimonial-slide-${tst.id}`}>
                  <div className="px-2 sm:px-14 py-4 text-center">
                    <Quote className="w-8 h-8 text-gold mx-auto mb-6 rotate-180" />
                    <p className="font-serif text-xl sm:text-2xl lg:text-[1.7rem] leading-relaxed text-ink text-balance">
                      “{tst.quote[lang]}”
                    </p>
                    <p className="mt-8 font-semibold text-ink text-sm tracking-wide">{tst.name}</p>
                    <p className="mt-1 flex items-center justify-center gap-2 text-xs text-[#8C96A4]">
                      <BadgeCheck className="w-3.5 h-3.5 text-gold" /> {tst.context[lang]}
                    </p>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex items-center justify-center gap-3 mt-6">
              <CarouselPrevious data-testid="testimonials-prev-btn" className="static translate-y-0 h-11 w-11 border-sand hover:border-gold" />
              <CarouselNext data-testid="testimonials-next-btn" className="static translate-y-0 h-11 w-11 border-sand hover:border-gold" />
            </div>
          </Carousel>
        </Reveal>
      </div>
    </section>
  );
};

const DevelopmentsSection = () => {
  const { t } = useLanguage();
  const { developments } = useDevelopments(fallbackDevelopments);
  const featured = developments.filter((d) => d.featured);
  const shown = (featured.length ? featured : developments).slice(0, 6);
  return (
    <section data-testid="developments-section" className="bg-ink grain-overlay relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-14 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <SectionHead dark overline={t.developments.overline} title={t.developments.title} subtitle={t.developments.subtitle} />
          <Reveal delay={0.2}>
            <Link to="/developments" data-testid="developments-view-all-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-cream link-underline-gold shrink-0">
              {t.developments.viewAll}
              <ArrowRight className="w-4 h-4 text-gold transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {shown.map((d, i) => (
            <Reveal key={d.id} delay={(i % 3) * 0.08}>
              <Link
                to={`/contact?development=${encodeURIComponent(d.name)}`}
                data-testid={`development-card-${d.id}`}
                className="group relative block aspect-[16/11] overflow-hidden border border-white/10"
              >
                <img
                  src={d.image}
                  alt={`${d.name}, ${d.area}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-transparent" />
                <span className="absolute top-4 left-4 text-[9px] uppercase tracking-[0.25em] font-semibold bg-gold text-ink px-3 py-1.5">
                  {d.area}
                </span>
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <p className="font-serif text-cream text-2xl leading-tight">{d.name}</p>
                  <p className="mt-1.5 text-cream/60 text-xs">{d.address}</p>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <p className="text-gold font-serif text-lg">
                      {t.developments.from} {fmtPrice(d.priceFrom)}
                    </p>
                    <span className="flex items-center gap-1.5 text-cream/70 text-xs font-semibold uppercase tracking-[0.15em] opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                      {t.developments.cta} <ArrowRight className="w-3.5 h-3.5 text-gold" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CtaBand = () => {
  const { t } = useLanguage();
  const { content } = useSiteContent();
  const phone = content?.phone || "(786) 774-6692";
  return (
    <section data-testid="closing-cta-band" className="bg-ink grain-overlay relative py-24 sm:py-32">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <Reveal>
          <h2 className="font-serif text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-balance">
            {t.ctaBand.title}
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-6 text-cream/60 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">{t.ctaBand.subtitle}</p>
        </Reveal>
        <Reveal delay={0.22}>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-5">
            <Link
              to="/contact"
              data-testid="cta-band-contact-btn"
              className="group inline-flex items-center gap-3 bg-gold hover:bg-gold-dark text-ink font-semibold text-sm tracking-wide px-9 py-4 rounded-full transition-colors duration-300"
            >
              {t.ctaBand.cta}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a data-testid="cta-band-call-link" href={`tel:+1${phone.replace(/\D/g, "")}`} className="text-cream/70 hover:text-gold text-sm font-medium transition-colors">
              {t.ctaBand.call} · {phone}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLanguage();
  return (
    <main data-testid="home-page">
      <Seo
        title="Carlos Vindel — Miami & South Florida Real Estate Agent"
        siteName="Carlos Vindel Realty"
        description="Every home tells a story — let's find yours. Carlos Vindel helps first-time buyers and residential clients across Miami & South Florida with hands-on, bilingual guidance."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "RealEstateAgent",
          name: "Carlos Vindel Realty",
          areaServed: "Miami, South Florida",
          telephone: "(786) 774-6692",
        }}
      />
      <Hero />
      <StatsBar />
      <AgentIntro />
      <FeaturedListings />
      <ValuationTeaser />
      <NeighborhoodsPreview />
      <DevelopmentsSection />
      <TestimonialsReel />
      <CtaBand />
    </main>
  );
}
